import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CurrencyIcon } from "@/components/CurrencyIcon";
import { confirmPayment, createPaymentIntent } from "@/lib/api";
import { toast } from "sonner";

type CheckoutFormProps = {
  amountLabel: string;
  currency: string;
  onPaid: () => void;
};

export const CheckoutForm = ({ amountLabel, currency, onPaid }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isPaying, setIsPaying] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || isPaying) return;

    setIsPaying(true);
    try {
      // Validates the card fields before we create anything server-side.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(submitError.message || 'Please check your card details.');
        return;
      }

      const { clientSecret, paymentIntentId } = await createPaymentIntent();

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          // Only used when the bank forces a redirect for 3-D Secure.
          return_url: `${window.location.origin}/`,
        },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || 'Payment failed. Please try again.');
        return;
      }

      // Never trust the client's word for it: the server re-checks with Stripe.
      const result = await confirmPayment(paymentIntent?.id || paymentIntentId);
      if (!result.paid) {
        toast.error('Payment could not be verified. Please try again.');
        return;
      }

      onPaid();
    } catch (err) {
      console.error('Payment error:', err);
      toast.error('Something went wrong taking your payment. Please try again.');
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="relative min-h-[190px]">
        {!isReady && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        )}
        <PaymentElement
        onReady={() => setIsReady(true)}
        options={{
          layout: 'tabs',
          // Deliberately no fields.billingDetails.email override here.
          // Opting out with 'never' obliges us to pass an email back in
          // confirmParams, which we do not have and do not want. The email
          // field only appears via Link, and the intent is card-only, so
          // leaving this alone is what actually keeps it off the form.
          //
          // GB merchant charging GBP; without this the country picker
          // defaults to Canada.
          defaultValues: { billingDetails: { address: { country: 'GB' } } },
        }}
        />
      </div>
      <Button
        type="submit"
        size="lg"
        disabled={!stripe || !isReady || isPaying}
        aria-busy={isPaying}
        className="w-full bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-60"
      >
        <span className="flex items-center gap-2">
          {isPaying ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Processing…
            </>
          ) : (
            <>
              <CurrencyIcon currency={currency} className="w-5 h-5" />
              Pay {amountLabel}
            </>
          )}
        </span>
      </Button>
      <p className="text-xs text-center text-gray-500">
        Secured by Stripe. No account or email required.
      </p>
    </form>
  );
};
